// ...existing code...
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import VesselVoyageForm from './VesselVoyageForm';
import './VesselVoyageList.css';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';

const API_BASE_URL = '/api/masters/vesselvoyages/';
const DETAIL_API_URL = '/api/masters/vessel-voyage-details/';
const SHIPPING_LINE_API_URL = '/api/masters/shippinglines/';
const PORT_OPERATOR_CHOICES = ['TEAGTL', 'DP WORLD'];

// Defined outside component so React never treats it as a new type on re-render
const ShippingLineSelect = ({ value, onChange, style, shippingLines }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={style}>
        <option value="">— None —</option>
        {shippingLines.map(sl => (
            <option key={sl.id} value={sl.id}>{sl.name}</option>
        ))}
    </select>
);

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json',
});

const fmtDate = dt => dt ? new Date(dt).toLocaleDateString('en-GB') : '—';
const fmtDateTime = dt => dt ? new Date(dt).toLocaleString('en-GB') : '—';

const VesselVoyageList = () => {
    const [vesselVoyages, setVesselVoyages] = useState([]);
    // ...existing state and hooks...

    // Duplicate ETA check for schedules (same vessel, same ETA)
    const isDuplicateEta = useCallback((vessel, eta, excludeScheduleId = null) => {
        const etaStr = eta ? new Date(eta).toISOString().slice(0, 10) : null;
        return vesselVoyages.some(vv =>
            vv.vessel.trim().toUpperCase() === vessel.trim().toUpperCase() &&
            vv.eta && new Date(vv.eta).toISOString().slice(0, 10) === etaStr &&
            (!excludeScheduleId || vv.id !== excludeScheduleId)
        );
    }, [vesselVoyages]);
    const [shippingLines, setShippingLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVessel, setSelectedVessel] = useState('');
    const [expandedVessels, setExpandedVessels] = useState(new Set());
    const [expandedSchedules, setExpandedSchedules] = useState(new Set());


    // ...existing code...

    // Place this useEffect after filteredVessels is defined
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);

    // Right-panel (schedule dates) inline edit
    const [editingScheduleId, setEditingScheduleId] = useState(null);
    const [scheduleEditFields, setScheduleEditFields] = useState({});
    const [savingSchedule, setSavingSchedule] = useState(false);

    // Left-panel (voyage detail) inline edit
    const [editingDetailId, setEditingDetailId] = useState(null);
    const [detailEditFields, setDetailEditFields] = useState({ voyage_number: '', shipping_line: '' });
    const [savingDetail, setSavingDetail] = useState(false);

    // Add new voyage detail
    const [addingDetailForVoyageId, setAddingDetailForVoyageId] = useState(null);
    const [newDetailFields, setNewDetailFields] = useState({ voyage_number: '', shipping_line: '' });

    const { hasPermission } = usePermissions();

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [vvRes, slRes] = await Promise.all([
                fetch(API_BASE_URL, { headers: getAuthHeaders() }),
                fetch(SHIPPING_LINE_API_URL, { headers: getAuthHeaders() }),
            ]);
            if (!vvRes.ok) throw new Error('Failed to fetch vessel voyages');
            if (!slRes.ok) throw new Error('Failed to fetch shipping lines');
            const [vvData, slData] = await Promise.all([vvRes.json(), slRes.json()]);
            setVesselVoyages(Array.isArray(vvData) ? vvData : (vvData.results || []));
            setShippingLines(Array.isArray(slData) ? slData : (slData.results || []));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Group by vessel name → list of VesselVoyage records sorted by ETA desc
    const groupedByVessel = useMemo(() => {
        const groups = {};
        vesselVoyages.forEach(vv => {
            const name = vv.vessel || '(Unknown)';
            if (!groups[name]) groups[name] = [];
            groups[name].push(vv);
        });
        Object.values(groups).forEach(arr =>
            arr.sort((a, b) => new Date(b.eta || 0) - new Date(a.eta || 0))
        );
        return groups;
    }, [vesselVoyages]);

    // Compute max vessel name width for dropdown
    const vesselOptions = useMemo(() => Object.keys(groupedByVessel).sort((a, b) => a.localeCompare(b)), [groupedByVessel]);
    const getMaxVesselWidth = () => {
        if (typeof window === 'undefined') return 200;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = '16px Arial';
        let max = 0;
        vesselOptions.forEach(opt => {
            const w = ctx.measureText(opt).width;
            if (w > max) max = w;
        });
        return Math.max(200, Math.ceil(max) + 40); // min 200px, add padding
    };
    const vesselDropdownWidth = getMaxVesselWidth();

    // Filtered vessels based on dropdown and search
    const filteredVessels = useMemo(() => {
        const q = searchTerm.toLowerCase();
        if (selectedVessel) {
            // Only show selected vessel if matches search
            if (!groupedByVessel[selectedVessel]) return [];
            if (!q) return [selectedVessel];
            // Check if any schedule for selected vessel matches search
            const matches = groupedByVessel[selectedVessel].some(vv =>
                vv.voyage_details.some(d =>
                    (d.voyage_number || '').toLowerCase().includes(q) ||
                    (d.shipping_line?.name || '').toLowerCase().includes(q)
                )
            );
            return matches ? [selectedVessel] : [];
        } else {
            // No vessel selected: filter all vessels by search
            return vesselOptions.filter(name => {
                if (name.toLowerCase().includes(q)) return true;
                return groupedByVessel[name].some(vv =>
                    vv.voyage_details.some(d =>
                        (d.voyage_number || '').toLowerCase().includes(q) ||
                        (d.shipping_line?.name || '').toLowerCase().includes(q)
                    )
                );
            });
        }
    }, [groupedByVessel, searchTerm, selectedVessel, vesselOptions]);

    // Duplicate check: same vessel + voyage_number + shipping_line
    const isDuplicate = useCallback((vessel, voyageNumber, shippingLineId, excludeDetailId = null) => {
        return vesselVoyages.some(vv =>
            vv.vessel === vessel &&
            vv.voyage_details.some(d =>
                d.id !== excludeDetailId &&
                d.voyage_number === voyageNumber &&
                String(d.shipping_line?.id || '') === String(shippingLineId || '')
            )
        );
    }, [vesselVoyages]);

    // Expand/collapse
    const toggleVessel = name => setExpandedVessels(prev => {
        const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next;
    });
    const toggleSchedule = id => setExpandedSchedules(prev => {
        const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });

    // ── Schedule right-panel inline edit ────────────────────────────────────
    const startEditSchedule = vv => {
        setEditingScheduleId(vv.id);
        setScheduleEditFields({
            eta: vv.eta || '',
            etb: vv.etb || '',
            atb: vv.atb || '',
            port_operator: vv.port_operator || '',
            port_operator_not_known: vv.port_operator_not_known || false,
            tra_manifest_ready: vv.tra_manifest_ready ? vv.tra_manifest_ready.slice(0, 16) : '',
        });
    };
    const cancelEditSchedule = () => setEditingScheduleId(null);

    const saveSchedule = async vv => {
        setSavingSchedule(true);
        try {
            const noOperator = !scheduleEditFields.port_operator;
            const body = {
                eta: scheduleEditFields.eta || null,
                etb: scheduleEditFields.etb || null,
                atb: scheduleEditFields.atb || null,
                port_operator: scheduleEditFields.port_operator || null,
                port_operator_not_known: noOperator,
                tra_manifest_ready: scheduleEditFields.tra_manifest_ready || null,
            };
            const res = await fetch(`${API_BASE_URL}${vv.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                const msg = err.detail ? (Array.isArray(err.detail) ? err.detail.join(' ') : err.detail)
                    : err.error || (Object.values(err)[0]) || 'Failed to save';
                throw new Error(Array.isArray(msg) ? msg.join(' ') : String(msg));
            }
            const updated = await res.json();
            setVesselVoyages(prev => prev.map(item => item.id === vv.id ? updated : item));
            toast.success('Schedule updated.');
            setEditingScheduleId(null);
        } catch (e) {
            toast.error('Failed to save: ' + e.message);
        } finally {
            setSavingSchedule(false);
        }
    };

    // ── Voyage detail left-panel inline edit ────────────────────────────────
    const startEditDetail = (vv, detail) => {
        setEditingDetailId(detail.id);
        setDetailEditFields({
            voyage_number: detail.voyage_number || '',
            shipping_line: detail.shipping_line?.id ? String(detail.shipping_line.id) : '',
            _vessel: vv.vessel,
        });
        setAddingDetailForVoyageId(null);
    };
    const cancelEditDetail = () => setEditingDetailId(null);

    const saveDetail = async (vv, detail) => {
        const { voyage_number, shipping_line, _vessel } = detailEditFields;
        if (!voyage_number.trim()) { toast.error('Voyage number is required.'); return; }
        if (isDuplicate(_vessel, voyage_number.trim(), shipping_line, detail.id)) {
            toast.error('A voyage with the same vessel, voyage number, and shipping line already exists.');
            return;
        }
        setSavingDetail(true);
        try {
            const res = await fetch(`${DETAIL_API_URL}${detail.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    voyage_number: voyage_number.trim(),
                    shipping_line_id: shipping_line || null,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const updatedDetail = await res.json();
            setVesselVoyages(prev => prev.map(item => {
                if (item.id !== vv.id) return item;
                return { ...item, voyage_details: item.voyage_details.map(d => d.id === detail.id ? updatedDetail : d) };
            }));
            toast.success('Voyage detail updated.');
            setEditingDetailId(null);
        } catch (e) {
            toast.error('Failed to save: ' + e.message);
        } finally {
            setSavingDetail(false);
        }
    };

    // ── Add new voyage detail ────────────────────────────────────────────────
    const startAddDetail = voyageId => {
        setAddingDetailForVoyageId(voyageId);
        setNewDetailFields({ voyage_number: '', shipping_line: '' });
        setEditingDetailId(null);
    };
    const cancelAddDetail = () => setAddingDetailForVoyageId(null);

    const saveNewDetail = async vv => {
        const { voyage_number, shipping_line } = newDetailFields;
        if (!voyage_number.trim()) { toast.error('Voyage number is required.'); return; }
        if (isDuplicate(vv.vessel, voyage_number.trim(), shipping_line)) {
            toast.error('A voyage with the same vessel, voyage number, and shipping line already exists.');
            return;
        }
        setSavingDetail(true);
        try {
            const res = await fetch(DETAIL_API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    vessel_voyage_id: vv.id,
                    voyage_number: voyage_number.trim(),
                    shipping_line_id: shipping_line || null,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const newDetail = await res.json();
            setVesselVoyages(prev => prev.map(item => {
                if (item.id !== vv.id) return item;
                return { ...item, voyage_details: [...item.voyage_details, newDetail] };
            }));
            toast.success('Voyage added.');
            setAddingDetailForVoyageId(null);
        } catch (e) {
            toast.error('Failed to add voyage: ' + e.message);
        } finally {
            setSavingDetail(false);
        }
    };

    const deleteDetail = async detailId => {
        if (!window.confirm('Delete this voyage entry?')) return;
        try {
            const res = await fetch(`${DETAIL_API_URL}${detailId}/`, {
                method: 'DELETE', headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete');
            setVesselVoyages(prev => prev.map(item => ({
                ...item,
                voyage_details: item.voyage_details.filter(d => d.id !== detailId),
            })));
            toast.success('Voyage deleted.');
        } catch (e) { toast.error(e.message); }
    };

    // ── Modal for add/edit full voyage ──────────────────────────────────────
    const openAddSchedule = vesselName => {
        setModalData({ vessel: vesselName, vesselReadOnly: true });
        setIsModalOpen(true);
    };
    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setModalData(null);
        if (refresh) fetchAll();
    };

    const [deleteDialog, setDeleteDialog] = useState({ open: false, schedule: null, jobIds: [], loading: false });

    // Fetch job IDs for a schedule (VesselVoyage)
    const fetchJobIdsForSchedule = async (scheduleId) => {
        try {
            setDeleteDialog(d => ({ ...d, loading: true }));
            const res = await fetch(`/api/documentation/vesselvoyage/${scheduleId}/jobs/`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch linked jobs');
            const data = await res.json();
            return data.job_ids || [];
        } catch (e) {
            toast.error(e.message);
            return [];
        } finally {
            setDeleteDialog(d => ({ ...d, loading: false }));
        }
    };

    // Delete schedule API
    const deleteSchedule = async (scheduleId) => {
        try {
            const res = await fetch(`${API_BASE_URL}${scheduleId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete schedule');
            setVesselVoyages(prev => prev.filter(item => item.id !== scheduleId));
            toast.success('Schedule deleted.');
        } catch (e) {
            toast.error(e.message);
        }
    };

    // ── Add New Vessel Modal ──────────────────────────────────────────────
    const [showAddVesselModal, setShowAddVesselModal] = useState(false);
    const [newVesselName, setNewVesselName] = useState('');
    const [newVesselEta, setNewVesselEta] = useState("");
    const [savingVessel, setSavingVessel] = useState(false);

    const handleSaveVessel = async () => {
        const vesselName = newVesselName.trim().toUpperCase();
        const eta = newVesselEta.trim();
        if (!vesselName) {
            toast.warn('Vessel name is required.');
            return;
        }
        if (!eta) {
            toast.warn('ETA is required.');
            return;
        }
        // Check for duplicate (case-insensitive)
        const exists = Object.keys(groupedByVessel).some(
            name => name.trim().toUpperCase() === vesselName
        );
        if (exists) {
            toast.error('Vessel name already exists.');
            return;
        }
        setSavingVessel(true);
        try {
            // Save vessel (create empty schedule with vessel name)
            const res = await fetch(API_BASE_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ vessel: vesselName, eta, port_operator_not_known: true })
            });
            if (!res.ok) throw new Error('Failed to add vessel');
            toast.success('Vessel added successfully!');
            setShowAddVesselModal(false);
            setNewVesselName('');
            setNewVesselEta("");
            fetchAll();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingVessel(false);
        }
    };

    // Auto-expand vessels and schedules that match search
    useEffect(() => {
        if (!searchTerm) return;
        const vesselsToExpand = new Set();
        const schedulesToExpand = new Set();
        filteredVessels.forEach(vesselName => {
            const schedules = groupedByVessel[vesselName] || [];
            let vesselHasMatch = false;
            schedules.forEach(vv => {
                const match = vv.voyage_details.some(d =>
                    (d.voyage_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (d.shipping_line?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
                );
                if (match) {
                    vesselHasMatch = true;
                    schedulesToExpand.add(vv.id);
                }
            });
            if (vesselHasMatch) vesselsToExpand.add(vesselName);
        });
        setExpandedVessels(vesselsToExpand);
        setExpandedSchedules(schedulesToExpand);
    }, [searchTerm, filteredVessels, groupedByVessel]);

    return (
        <div className="vessel-voyage-list-container">
            <h2>Vessel Voyages List</h2>
            {loading && <p className="loading-message">Loading vessel voyages...</p>}


            <div className="toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, width: vesselDropdownWidth }}>
                    <Select2Dropdown
                        options={vesselOptions}
                        value={selectedVessel}
                        onChange={val => { setSelectedVessel(val); setSearchTerm(''); }}
                        placeholder="Select Vessel..."
                        style={{ width: vesselDropdownWidth, minWidth: vesselDropdownWidth, maxWidth: vesselDropdownWidth }}
                        clearable={true}
                    />
                </div>
                <input
                    type="text"
                    placeholder="Search Voyage Numbers or Shipping Lines"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{ width: 280, minWidth: 180, marginLeft: 8, flexShrink: 0 }}
                />
                {hasPermission('VESSEL VOYAGES', 'can_create') && (
                    <button onClick={() => setShowAddVesselModal(true)} style={{ marginLeft: 'auto', minWidth: 200 }}>
                        Add New Vessel
                    </button>
                )}
            </div>


            {showAddVesselModal && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: '90vw', maxWidth: '400px' }}>
                        <div className="dialog-header">
                            <h3>Add New Vessel</h3>
                            <button className="icon-btn" onClick={() => { setShowAddVesselModal(false); setNewVesselName(''); setNewVesselEta(""); }} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body">
                            <div className="form-group">
                                <label>Vessel Name <span style={{color: 'red'}}>*</span></label>
                                <input
                                    type="text"
                                    value={newVesselName}
                                    onChange={e => setNewVesselName(e.target.value.toUpperCase())}
                                    className="form-control"
                                    required
                                    autoFocus
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>ETA <span style={{color: 'red'}}>*</span></label>
                                <input
                                    type="date"
                                    value={newVesselEta}
                                    onChange={e => setNewVesselEta(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => { setShowAddVesselModal(false); setNewVesselName(''); setNewVesselEta(""); }}>Cancel</button>
                            <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSaveVessel} disabled={savingVessel}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {!loading && (
                filteredVessels.length === 0
                    ? <div className="no-data">No vessel voyages found.</div>
                    : (
                        <div className="table-container">
                            <table className="vessel-voyage-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '28%' }}>Vessel</th>
                                        <th>Schedules</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredVessels.map(vesselName => {
                                        const schedules = groupedByVessel[vesselName];
                                        const isVesselExpanded = expandedVessels.has(vesselName);

                                        return (
                                            <React.Fragment key={vesselName}>
                                                {/* ── Vessel row ── */}
                                                <tr className="vessel-row">
                                                    <td className="vessel-name">
                                                        <strong>{vesselName}</strong>
                                                        <div className="vessel-meta">{schedules.length} schedule(s)</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <button className="expand-btn" onClick={() => toggleVessel(vesselName)}>
                                                                <i className={`fa ${isVesselExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                                {isVesselExpanded ? ' Hide' : ' Show'} Schedules
                                                            </button>
                                                            {hasPermission('VESSEL VOYAGES', 'can_create') && (
                                                                <button
                                                                    className="expand-btn"
                                                                    style={{ background: '#3f6b32', color: '#fff', border: 'none' }}
                                                                    onClick={() => openAddSchedule(vesselName)}
                                                                >
                                                                    <i className="fa fa-plus" /> Add Schedule
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* ── Schedule rows ── */}
                                                {isVesselExpanded && schedules.map(vv => {
                                                    const isSchExpanded = expandedSchedules.has(vv.id);
                                                    const isEditingSch = editingScheduleId === vv.id;

                                                    return (
                                                        <React.Fragment key={vv.id}>
                                                            <tr className="schedule-row">
                                                                <td colSpan={2} style={{ padding: '6px 12px' }}>

                                                                    {/* ── Schedule header: always visible ── */}
                                                                    {isEditingSch ? (
                                                                        /* Edit mode: inline fields in a single row */
                                                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                                                                            <button className="expand-btn voyage-expand-btn" onClick={() => toggleSchedule(vv.id)}>
                                                                                <i className={`fa ${isSchExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                                                {isSchExpanded ? ' Hide' : ' Show'} Voyages
                                                                            </button>
                                                                            {[
                                                                                { label: 'ETA', key: 'eta' },
                                                                                { label: 'ETB', key: 'etb' },
                                                                                { label: 'ATB', key: 'atb' },
                                                                            ].map(({ label, key }) => (
                                                                                <label key={key} style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                    {label}
                                                                                    <input
                                                                                        type="date"
                                                                                        value={scheduleEditFields[key] || ''}
                                                                                        onChange={e => setScheduleEditFields(f => ({ ...f, [key]: e.target.value }))}
                                                                                        style={{ minWidth: 130 }}
                                                                                    />
                                                                                </label>
                                                                            ))}
                                                                            <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                Port Terminal
                                                                                <select
                                                                                    value={scheduleEditFields.port_operator || ''}
                                                                                    onChange={e => setScheduleEditFields(f => ({
                                                                                        ...f,
                                                                                        port_operator: e.target.value,
                                                                                        port_operator_not_known: !e.target.value,
                                                                                    }))}
                                                                                    style={{ minWidth: 130 }}
                                                                                >
                                                                                    <option value="">Not Known</option>
                                                                                    {PORT_OPERATOR_CHOICES.map(o => <option key={o} value={o}>{o}</option>)}
                                                                                </select>
                                                                            </label>
                                                                            <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                Manifest Date
                                                                                <input
                                                                                    type="datetime-local"
                                                                                    value={scheduleEditFields.tra_manifest_ready || ''}
                                                                                    onChange={e => setScheduleEditFields(f => ({ ...f, tra_manifest_ready: e.target.value }))}
                                                                                    style={{ minWidth: 170 }}
                                                                                />
                                                                            </label>
                                                                            <div style={{ display: 'flex', gap: 6 }}>
                                                                                <button className="icon-btn save-btn" disabled={savingSchedule} onClick={() => saveSchedule(vv)} title="Save">
                                                                                    <i className="fa fa-save" /> Save
                                                                                </button>
                                                                                <button className="icon-btn cancel-btn" onClick={cancelEditSchedule} title="Cancel">
                                                                                    <i className="fa fa-times" /> Cancel
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        /* View mode: two lines — schedule fields on line 1, voyages toggle on line 2 */
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                                                            {/* Line 1: ETA ETB ATB Port Terminal Manifest Date + Edit */}
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                                                                <span style={{ color: '#555' }}><strong>ETA:</strong> {fmtDate(vv.eta)}</span>
                                                                                <span style={{ color: '#555' }}><strong>ETB:</strong> {fmtDate(vv.etb)}</span>
                                                                                <span style={{ color: '#555' }}><strong>ATB:</strong> {fmtDate(vv.atb)}</span>
                                                                                <span style={{ color: '#555' }}><strong>Port Terminal:</strong> {vv.port_operator_not_known ? 'Not Known' : (vv.port_operator || '—')}</span>
                                                                                <span style={{ color: '#555' }}><strong>Manifest Date:</strong> {fmtDateTime(vv.tra_manifest_ready)}</span>
                                                                                {hasPermission('VESSEL VOYAGES', 'can_edit') && (
                                                                                    <button className="icon-btn edit-btn" onClick={() => startEditSchedule(vv)} title="Edit Schedule">
                                                                                        <i className="fa fa-pencil-alt" />
                                                                                    </button>
                                                                                )}
                                                                                {hasPermission('VESSEL VOYAGES', 'can_delete') && (
                                                                                    <button
                                                                                        className="icon-btn delete-btn"
                                                                                        title="Delete Schedule"
                                                                                        style={{ color: '#c0392b' }}
                                                                                        onClick={async () => {
                                                                                            setDeleteDialog({ open: true, schedule: vv, jobIds: [], loading: true });
                                                                                            const jobIds = await fetchJobIdsForSchedule(vv.id);
                                                                                            setDeleteDialog({ open: true, schedule: vv, jobIds, loading: false });
                                                                                        }}
                                                                                    >
                                                                                        <i className="fa fa-trash" />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                            {/* Line 2: Show Voyages toggle + voyage numbers */}
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                <button className="expand-btn voyage-expand-btn" onClick={() => toggleSchedule(vv.id)}>
                                                                                    <i className={`fa ${isSchExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                                                    {isSchExpanded ? ' Hide' : ' Show'} Voyages
                                                                                </button>
                                                                                {vv.voyage_details.length > 0 && (
                                                                                    <span style={{ color: '#555' }}><strong>Voyage(s):</strong> {vv.voyage_details.map(d => d.voyage_number).join(', ')}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* ── Expanded: Voyage Numbers & Shipping Lines only ── */}
                                                                    {isSchExpanded && (
                                                                        <div style={{ marginTop: 10 }}>
                                                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#2f3b52' }}>
                                                                                Voyage Numbers &amp; Shipping Lines
                                                                            </div>
                                                                            <table style={{ width: 'max-content', fontSize: 12, borderCollapse: 'collapse' }}>
                                                                                <thead>
                                                                                    <tr style={{ background: '#eef2f7' }}>
                                                                                        <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ddd' }}>Voyage No.</th>
                                                                                        <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #ddd' }}>Shipping Line</th>
                                                                                        {hasPermission('VESSEL VOYAGES', 'can_edit') && (
                                                                                            <th style={{ padding: '4px 8px', border: '1px solid #ddd', width: 70 }}>Actions</th>
                                                                                        )}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {vv.voyage_details.map(detail => (
                                                                                        <tr key={detail.id} style={{ borderBottom: '1px solid #eee' }}>
                                                                                            {editingDetailId === detail.id ? (
                                                                                                <>
                                                                                                    <td style={{ padding: 4, border: '1px solid #ddd' }}>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={detailEditFields.voyage_number}
                                                                                                            onChange={e => setDetailEditFields(f => ({ ...f, voyage_number: e.target.value }))}
                                                                                                            style={{ width: '100%' }}
                                                                                                            autoFocus
                                                                                                        />
                                                                                                    </td>
                                                                                                    <td style={{ padding: 4, border: '1px solid #ddd' }}>
                                                                                                        <ShippingLineSelect
                                                                                                            value={detailEditFields.shipping_line}
                                                                                                            onChange={v => setDetailEditFields(f => ({ ...f, shipping_line: v }))}
                                                                                                            style={{ width: '100%' }}
                                                                                                            shippingLines={shippingLines}
                                                                                                        />
                                                                                                    </td>
                                                                                                    <td style={{ padding: 4, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                                                                                                        <button className="icon-btn save-btn" disabled={savingDetail} onClick={() => saveDetail(vv, detail)} title="Save">
                                                                                                            <i className="fa fa-save" />
                                                                                                        </button>
                                                                                                        <button className="icon-btn cancel-btn" onClick={cancelEditDetail} title="Cancel">
                                                                                                            <i className="fa fa-times" />
                                                                                                        </button>
                                                                                                    </td>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{detail.voyage_number}</td>
                                                                                                    <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{detail.shipping_line?.name || '—'}</td>
                                                                                                    {hasPermission('VESSEL VOYAGES', 'can_edit') && (
                                                                                                        <td style={{ padding: 4, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                                                                                                            <button className="icon-btn edit-btn" onClick={() => startEditDetail(vv, detail)} title="Edit">
                                                                                                                <i className="fa fa-pencil-alt" />
                                                                                                            </button>
                                                                                                            {hasPermission('VESSEL VOYAGES', 'can_delete') && (
                                                                                                                <button className="icon-btn delete-btn" onClick={() => deleteDetail(detail.id)} title="Delete">
                                                                                                                    <i className="fa fa-trash" />
                                                                                                                </button>
                                                                                                            )}
                                                                                                        </td>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </tr>
                                                                                    ))}
                                                                                    {/* New detail input row */}
                                                                                    {addingDetailForVoyageId === vv.id && (
                                                                                        <tr style={{ background: '#f9fff9' }}>
                                                                                            <td style={{ padding: 4, border: '1px solid #ddd' }}>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    placeholder="Voyage Number"
                                                                                                    value={newDetailFields.voyage_number}
                                                                                                    onChange={e => setNewDetailFields(f => ({ ...f, voyage_number: e.target.value }))}
                                                                                                    style={{ width: '100%' }}
                                                                                                    autoFocus
                                                                                                />
                                                                                            </td>
                                                                                            <td style={{ padding: 4, border: '1px solid #ddd' }}>
                                                                                                <ShippingLineSelect
                                                                                                    value={newDetailFields.shipping_line}
                                                                                                    onChange={v => setNewDetailFields(f => ({ ...f, shipping_line: v }))}
                                                                                                    style={{ width: '100%' }}
                                                                                                    shippingLines={shippingLines}
                                                                                                />
                                                                                            </td>
                                                                                            <td style={{ padding: 4, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                                                                                                <button className="icon-btn save-btn" disabled={savingDetail} onClick={() => saveNewDetail(vv)} title="Save">
                                                                                                    <i className="fa fa-save" />
                                                                                                </button>
                                                                                                <button className="icon-btn cancel-btn" onClick={cancelAddDetail} title="Cancel">
                                                                                                    <i className="fa fa-times" />
                                                                                                </button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                            {hasPermission('VESSEL VOYAGES', 'can_create') && addingDetailForVoyageId !== vv.id && (
                                                                                <button
                                                                                    className="expand-btn"
                                                                                    style={{ marginTop: 8, background: '#2980b9', color: '#fff', border: 'none' }}
                                                                                    onClick={() => startAddDetail(vv.id)}
                                                                                >
                                                                                    <i className="fa fa-plus" /> Add Voyage
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>&times;</button>
                        <VesselVoyageForm
                            mode="add"
                            initialData={modalData ? { vessel: modalData.vessel } : null}
                            vesselReadOnly={modalData?.vesselReadOnly || false}
                            onClose={handleFormClose}
                            isDuplicateEta={isDuplicateEta}
                            editingScheduleId={editingScheduleId}
                        />
                    </div>
                </div>
            )}

            {/* ConfirmDialog for delete confirmation or job linkage alert */}
            <ConfirmDialog
                open={deleteDialog.open}
                title={deleteDialog.jobIds.length === 0 ? 'Delete Schedule' : 'Cannot Delete Schedule'}
                message={deleteDialog.loading ? 'Checking for linked jobs...' : (
                    deleteDialog.jobIds.length === 0
                        ? 'Are you sure you would like to Delete this Schedule?'
                        : <span>This Schedule cannot be deleted as it is linked to Job ID {deleteDialog.jobIds.join(', ')}.</span>
                )}
                okOnly={deleteDialog.jobIds.length > 0}
                onCancel={() => setDeleteDialog({ open: false, schedule: null, jobIds: [], loading: false })}
                onConfirm={async () => {
                    if (deleteDialog.jobIds.length === 0 && deleteDialog.schedule) {
                        await deleteSchedule(deleteDialog.schedule.id);
                        setDeleteDialog({ open: false, schedule: null, jobIds: [], loading: false });
                    } else {
                        setDeleteDialog({ open: false, schedule: null, jobIds: [], loading: false });
                    }
                }}
            />
        </div>
    );
};

export default VesselVoyageList;

